import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import {
  FailoverItem,
  MaintenanceItem,
  OverviewServiceLabel,
  Port,
  ServiceStatus,
  ServiceStatusItem,
} from "../Types";

/**
 * Pure derivations behind the Overview action center (CTX-598).
 *
 * Every block on the page reads one of the four payloads the overview already
 * downloads (service statuses, usage, failover history, maintenance); nothing
 * here fetches. Keeping the rules out of the components is what lets the
 * prototype be judged — and tested — on the numbers, not on the pixels.
 */

export type ServiceKindKey = keyof ServiceStatus;

/** What the scope chips filter by: one service kind, or all of them. */
export type ServiceScope = "all" | ServiceKindKey;

export interface ServiceKindInfo {
  key: ServiceKindKey;
  label: string;
  /** List view the chip and the attention rows link to. */
  path: string;
  /** The `Port.allocated` bucket this kind consumes, when it consumes one. */
  allocationKey: AllocationKey | null;
}

/** Display order of the chips; the same order the old Service Status grid used. */
export const SERVICE_KINDS: ServiceKindInfo[] = [
  { key: "ports", label: "Ports", path: "/kontrol/port", allocationKey: null },
  { key: "internet", label: "Internet", path: "/kontrol/ipx", allocationKey: "internet" },
  { key: "cloudConnectivity", label: "Cloud", path: "/kontrol/cloud-connectivity", allocationKey: "cloudConnectivity" },
  { key: "datacenterInterconnect", label: "DC Interconnect", path: "/kontrol/datacenter", allocationKey: "datacenterInterconnect" },
  { key: "fwaas", label: "Firewall", path: "/kontrol/fwaas", allocationKey: "fwint" },
  { key: "sdwan", label: "SD-WAN", path: "/kontrol/sdwan-hub", allocationKey: null },
];

const KIND_BY_KEY = new Map(SERVICE_KINDS.map((kind) => [kind.key, kind]));

export function kindInfo(key: ServiceKindKey): ServiceKindInfo {
  // Every ServiceStatus key is listed above, so the lookup cannot miss.
  return KIND_BY_KEY.get(key) as ServiceKindInfo;
}

// ---------------------------------------------------------------------------
// Health of one status string
// ---------------------------------------------------------------------------

export type ServiceHealth = "up" | "warning" | "down" | "unknown";

const UP_STATUSES = new Set(["up", "active", "connected"]);
const DOWN_STATUSES = new Set(["down", "inactive", "disconnected"]);
const WARNING_STATUSES = new Set(["warning"]);

/**
 * One status string, as the overview understands it.
 *
 * `unknown` is not a failure: since CTX-544 the backend sends it when it could
 * not read the telemetry, and painting it red is what made a healthy network
 * look down. It never counts as something that needs attention.
 */
export function classifyServiceStatus(status?: string | null): ServiceHealth {
  const normalized = (status ?? "").toString().trim().toLowerCase();
  if (UP_STATUSES.has(normalized)) return "up";
  if (DOWN_STATUSES.has(normalized)) return "down";
  if (WARNING_STATUSES.has(normalized)) return "warning";
  return "unknown";
}

// ---------------------------------------------------------------------------
// Tenant scope
// ---------------------------------------------------------------------------

export interface TenantScope {
  tenantId: number;
  /** A partner (or a Global Admin on a partner) looking at every customer at once. */
  isViewingAllCustomers: boolean;
}

/** The same rule the classic overview applies to each of its four payloads. */
export function isInTenantScope(
  item: { customer?: { id: number } | null },
  scope: TenantScope
): boolean {
  return scope.isViewingAllCustomers || item.customer?.id === scope.tenantId;
}

// ---------------------------------------------------------------------------
// Per-kind health (the scope chips)
// ---------------------------------------------------------------------------

export interface KindHealth {
  key: ServiceKindKey;
  label: string;
  total: number;
  up: number;
  warning: number;
  down: number;
  unknown: number;
}

export function summarizeKinds(
  serviceStatus: ServiceStatus | null,
  scope: TenantScope
): KindHealth[] {
  return SERVICE_KINDS.map(({ key, label }) => {
    const items = (serviceStatus?.[key] ?? []).filter((item) => isInTenantScope(item, scope));
    const counts = { up: 0, warning: 0, down: 0, unknown: 0 };
    for (const item of items) counts[classifyServiceStatus(item.status)] += 1;
    return { key, label, total: items.length, ...counts };
  });
}

/** The worst state among a kind's services, for the dot on its chip. */
export function worstHealth(kind: Pick<KindHealth, "total" | "down" | "warning" | "up">): ServiceHealth {
  if (kind.down > 0) return "down";
  if (kind.warning > 0) return "warning";
  if (kind.up > 0) return "up";
  return "unknown";
}

// ---------------------------------------------------------------------------
// Sites (one per datacenter that holds a port)
// ---------------------------------------------------------------------------

export interface SiteSummary {
  id: string;
  /** The ports' own names, which the customer chose and recognises. */
  portNames: string[];
  ports: number;
  portsDown: number;
  capacityMbps: number;
  allocatedMbps: number;
}

export function allocatedMbps(port: Pick<Port, "allocated">): number {
  return Object.values(port.allocated ?? {}).reduce<number>(
    (sum, value) => sum + Number(value ?? 0),
    0
  );
}

/**
 * One summary per datacenter. No service count on purpose: `servicesCount` is
 * per port and virtual ports report 0 even when cloud services ride on them
 * (staging, 2026-10-03), so a per-site total would contradict the chips.
 */
export function summarizeSites(ports: Port[]): SiteSummary[] {
  const byId = new Map<string, SiteSummary>();
  for (const port of ports) {
    const id = port.datacenterId || "Unassigned";
    const site =
      byId.get(id) ??
      { id, portNames: [], ports: 0, portsDown: 0, capacityMbps: 0, allocatedMbps: 0 };
    site.ports += 1;
    if (port.name) site.portNames.push(port.name);
    if (classifyServiceStatus(port.state?.operationalState) === "down") site.portsDown += 1;
    site.capacityMbps += Number(port.capacity ?? 0);
    site.allocatedMbps += allocatedMbps(port);
    byId.set(id, site);
  }
  return [...byId.values()].sort((a, b) => a.id.localeCompare(b.id));
}

// ---------------------------------------------------------------------------
// Record dates
// ---------------------------------------------------------------------------

/**
 * Failover and maintenance records carry `date` as the backend's
 * `toLocaleDateString()` — a date with no time, e.g. "10/3/2026".
 */
export function parseRecordDate(date: string | null | undefined): Date | null {
  if (!date) return null;
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** How far back a resolved outage still earns a row in "Attention required". */
export const RECENT_OUTAGE_DAYS = 7;

// ---------------------------------------------------------------------------
// The service list (right of the map)
// ---------------------------------------------------------------------------

/**
 * `critical` / `warning`: needs attention. `resolved`: an outage that closed
 * this week. `nodata`: no telemetry, which is not a failure (CTX-544).
 * `ok`: up.
 */
export type AttentionSeverity = "critical" | "warning" | "resolved" | "nodata" | "ok";

export interface AttentionItem {
  key: string;
  severity: AttentionSeverity;
  /** Short state shown in the badge. */
  state: string;
  /** The name the customer gave the service, else its subscription id. */
  title: string;
  context: string;
  /** The customer's own tags on the service, searchable like the rest. */
  tags: string[];
  /** "Cloud", "Ports", ... or "Outage": what the list filter calls Type. */
  type: string;
  /** Readable site(s), when known. */
  site: string | null;
  /** The site ids the row belongs to, so a click on the map can filter the list. */
  siteIds: string[];
  customer: string | null;
  /** Where clicking the row goes, when it goes somewhere. */
  path: string | null;
  /** The outage behind the row, for its detail modal. */
  outage: FailoverItem | null;
}

/** Open problems first; healthy services last, so the list can hold all of them. */
const SEVERITY_RANK: Record<AttentionSeverity, number> = {
  critical: 0,
  warning: 1,
  resolved: 2,
  nodata: 3,
  ok: 4,
};

export function isOpenIssue(item: AttentionItem): boolean {
  return item.severity === "critical" || item.severity === "warning";
}

/** Service kind → service id → the site ids it sits on (overview endpoint). */
export type ServicePlacements = Partial<Record<ServiceKindKey, Record<string, string[]>>>;

/** Service kind → service id → its customer-given name and tags (overview endpoint). */
export type ServiceLabels = Partial<Record<ServiceKindKey, Record<string, OverviewServiceLabel>>>;

const SERVICE_STATE: Record<ServiceHealth, { severity: AttentionSeverity; state: string }> = {
  down: { severity: "critical", state: "Down" },
  warning: { severity: "warning", state: "Degraded" },
  unknown: { severity: "nodata", state: "No data" },
  up: { severity: "ok", state: "Up" },
};

/**
 * The detail view of one service, so a row opens it — and, through
 * `data-view-href`, can open it in a view of its own (right click "Add view").
 * Firewalls have no detail route; their row opens the list.
 */
export function serviceDetailPath(kind: ServiceKindInfo, item: ServiceStatusItem): string {
  // The owner travels with the address, as the service lists do, so a partner
  // opening a child's service lands in that child's scope.
  const extra = item.customer?.id ? { tenantId: item.customer.id } : {};
  switch (kind.key) {
    case "ports":
      return buildDetailPath("port", { portId: item.id }, extra);
    case "internet":
      return buildDetailPath("internet", { connectionId: item.id }, extra);
    case "cloudConnectivity":
      return buildDetailPath("cloudConnectivity", { connectionId: item.id }, extra);
    case "datacenterInterconnect":
      return buildDetailPath("datacenter", { datacenterId: item.id }, extra);
    case "sdwan":
      return buildDetailPath("sdwanHub", { sdwanId: item.id }, extra);
    default:
      return kind.path;
  }
}

function serviceRow(
  kind: ServiceKindInfo,
  item: ServiceStatusItem,
  showCustomer: boolean,
  siteLabel: string | null,
  siteIds: string[],
  label: OverviewServiceLabel | null
): AttentionItem {
  const { severity, state } = SERVICE_STATE[classifyServiceStatus(item.status)];
  const subscriptionId = item.subscriptionId?.trim() || null;
  // A name the customer chose is what they recognise; the id moves to the second
  // line, where it is still read (support quotes it) and found by the search.
  const name = label?.name?.trim() || null;
  return {
    key: `${kind.key}-${item.id}`,
    severity,
    state,
    title: name ?? subscriptionId ?? "Unnamed service",
    context: [name && name !== subscriptionId ? subscriptionId : null, kind.label, siteLabel].filter(Boolean).join(" · "),
    tags: label?.tags ?? [],
    type: kind.label,
    site: siteLabel,
    siteIds,
    customer: showCustomer ? item.customer?.name ?? null : null,
    path: serviceDetailPath(kind, item),
    outage: null,
  };
}

function outageAttention(
  outage: FailoverItem,
  index: number,
  showCustomer: boolean
): AttentionItem {
  const isOngoing = outage.duration == null;
  const where = outage.site?.trim() || "Unknown site";
  // Never `summary`: it names our routers ("Router KE.MXQRO01.RT02 unreachable"),
  // which no customer-facing screen shows. The row says only what the outages
  // table already does — site, date, incident id, customer.
  const reference = outage.incidentId?.trim();
  return {
    // One incident hits several customers, each with its own record: the
    // incident id and date repeat, the record's position does not.
    key: `outage-${index}-${outage.incidentId ?? ""}-${outage.customer?.id ?? ""}`,
    severity: isOngoing ? "critical" : "resolved",
    state: isOngoing ? "Ongoing" : "Resolved",
    title: `Outage at ${where}`,
    tags: [],
    type: "Outage",
    site: where,
    // Outages name their site in their own words, not by datacenter id.
    siteIds: [],
    context: [reference, isOngoing ? `since ${outage.date}` : `${outage.date} · lasted ${formatDuration(outage.duration)}`]
      .filter(Boolean)
      .join(" · "),
    customer: showCustomer ? outage.customer?.name ?? null : null,
    path: null,
    outage,
  };
}

/**
 * Every service of the selected kind (all kinds under "All services"), worst
 * first: outages still open and services down, then degraded services, then
 * outages that closed this week, then services with no data, then the ones up.
 * Each service names the site it sits on when the overview endpoint placed it.
 *
 * Outages belong to a site, not to a service kind, so they stay visible under
 * every scope: hiding an open outage because the chip says "Internet" would be
 * the one wrong answer this list can give.
 */
export function buildAttentionItems({
  serviceStatus,
  failoverRecords,
  scope,
  tenantScope,
  now,
  placements = {},
  siteNames = {},
  labels = {},
}: {
  serviceStatus: ServiceStatus | null;
  failoverRecords: FailoverItem[];
  scope: ServiceScope;
  tenantScope: TenantScope;
  now: Date;
  placements?: ServicePlacements;
  /** Site id → readable name. */
  siteNames?: Record<string, string>;
  labels?: ServiceLabels;
}): AttentionItem[] {
  const showCustomer = tenantScope.isViewingAllCustomers;
  const items: AttentionItem[] = [];
  const sitesOf = (kind: ServiceKindKey, serviceId: string): string[] => placements[kind]?.[String(serviceId)] ?? [];
  const siteLabel = (sites: string[]): string | null =>
    sites.length === 0 ? null : sites.map((site) => siteNames[site] ?? site).join(" ↔ ");

  for (const kind of SERVICE_KINDS) {
    if (scope !== "all" && scope !== kind.key) continue;
    for (const item of serviceStatus?.[kind.key] ?? []) {
      if (!isInTenantScope(item, tenantScope)) continue;
      const sites = sitesOf(kind.key, item.id);
      const label = labels[kind.key]?.[String(item.id)] ?? null;
      items.push(serviceRow(kind, item, showCustomer, siteLabel(sites), sites, label));
    }
  }

  const recentSince = startOfDay(now).getTime() - RECENT_OUTAGE_DAYS * DAY_MS;
  failoverRecords.forEach((outage, index) => {
    if (!isInTenantScope(outage, tenantScope)) return;
    const isOngoing = outage.duration == null;
    const day = parseRecordDate(outage.date);
    const isRecent = day !== null && day.getTime() >= recentSince;
    if (isOngoing || isRecent) items.push(outageAttention(outage, index, showCustomer));
  });

  // Stable: within a severity, services keep their kind order and outages the
  // backend's newest-first order.
  return items
    .map((item, order) => ({ item, order }))
    .sort((a, b) => SEVERITY_RANK[a.item.severity] - SEVERITY_RANK[b.item.severity] || a.order - b.order)
    .map(({ item }) => item);
}

const HEALTH_SEVERITY: Record<ServiceHealth, number> = { down: 3, warning: 2, up: 1, unknown: 0 };

/** The worse of two states; "no data" never outranks a reading. */
export function worseHealth(a: ServiceHealth, b: ServiceHealth): ServiceHealth {
  return HEALTH_SEVERITY[b] > HEALTH_SEVERITY[a] ? b : a;
}

/**
 * Each site's worst service state, from the services that sit on it (through
 * `placements`), counting only the kinds in `scope`. A degraded cloud service
 * colours the site it leaves from; under the "Internet" chip it does not.
 */
export function serviceHealthBySite({
  serviceStatus,
  placements,
  scope,
  tenantScope,
}: {
  serviceStatus: ServiceStatus | null;
  placements: ServicePlacements;
  scope: ServiceScope;
  tenantScope: TenantScope;
}): Record<string, ServiceHealth> {
  const health: Record<string, ServiceHealth> = {};
  for (const kind of SERVICE_KINDS) {
    if (scope !== "all" && scope !== kind.key) continue;
    for (const item of serviceStatus?.[kind.key] ?? []) {
      if (!isInTenantScope(item, tenantScope)) continue;
      const state = classifyServiceStatus(item.status);
      for (const site of placements[kind.key]?.[String(item.id)] ?? []) {
        health[site] = worseHealth(health[site] ?? "unknown", state);
      }
    }
  }
  return health;
}

export type StatusFilter = "all" | "issues" | "up" | "nodata";

const STATUS_MATCH: Record<StatusFilter, (item: AttentionItem) => boolean> = {
  all: () => true,
  issues: isOpenIssue,
  up: (item) => item.severity === "ok",
  nodata: (item) => item.severity === "nodata",
};

/** How many rows each status pill would leave. */
export function countByStatus(items: AttentionItem[]): Record<StatusFilter, number> {
  return {
    all: items.length,
    issues: items.filter(STATUS_MATCH.issues).length,
    up: items.filter(STATUS_MATCH.up).length,
    nodata: items.filter(STATUS_MATCH.nodata).length,
  };
}

/**
 * The service list's filters, all optional and combined: a status pill, a
 * plain search (no syntax: it looks in the service's name, id, tags, type,
 * site, state and customer) and the site picked on the map.
 */
export function filterServiceList(
  items: AttentionItem[],
  { status = "all", query = "", siteIds = [] }: { status?: StatusFilter; query?: string; siteIds?: string[] }
): AttentionItem[] {
  const needle = query.trim().toLowerCase();
  const sites = new Set(siteIds);
  return items.filter(
    (item) =>
      STATUS_MATCH[status](item) &&
      (sites.size === 0 || item.siteIds.some((site) => sites.has(site))) &&
      (needle === "" ||
        [item.title, item.context, item.type, item.site, item.state, item.customer, ...item.tags].some((value) =>
          value?.toLowerCase().includes(needle)
        ))
  );
}

/**
 * The sites a scope covers: every site under "All services" or "Ports", or the
 * sites holding at least one service of the selected kind.
 */
export function sitesInScope(
  scope: ServiceScope,
  allSiteIds: string[],
  placements: ServicePlacements
): Set<string> {
  if (scope === "all" || scope === "ports") return new Set(allSiteIds);
  return new Set(Object.values(placements[scope] ?? {}).flat());
}

/**
 * The newest outage in scope, for the all-clear state: "nothing is open" reads
 * as a fact, not as an empty box, when it says when the last one was.
 * The backend sends the records newest first.
 */
export function lastOutage(failoverRecords: FailoverItem[], tenantScope: TenantScope): FailoverItem | null {
  return failoverRecords.find((outage) => isInTenantScope(outage, tenantScope)) ?? null;
}

// ---------------------------------------------------------------------------
// Capacity allocation
// ---------------------------------------------------------------------------

export interface AllocationRow {
  key: AllocationKey;
  label: string;
  allocatedMbps: number;
}

export interface AllocationSummary {
  capacityMbps: number;
  allocatedMbps: number;
  rows: AllocationRow[];
}

export type AllocationKey = "internet" | "cloudConnectivity" | "datacenterInterconnect" | "backbone" | "fwint";

/**
 * The buckets the classic "Capacity allocation" chart reads. The payload also
 * sends per-provider cloud keys (`awsDirectConnect`, ...); they are a breakdown,
 * not extra bandwidth, so adding them here would count cloud twice.
 */
const ALLOCATION_LABELS: Record<AllocationKey, string> = {
  internet: "Enterprise Internet",
  cloudConnectivity: "Cloud Connectivity",
  datacenterInterconnect: "DC Interconnect",
  backbone: "Backbone",
  fwint: "Firewall",
};

/** Allocated bandwidth per service type against the total capacity of the ports. */
export function summarizeAllocation(ports: Port[]): AllocationSummary {
  const totals: Record<AllocationKey, number> = {
    internet: 0,
    cloudConnectivity: 0,
    datacenterInterconnect: 0,
    backbone: 0,
    fwint: 0,
  };
  let capacityMbps = 0;
  for (const port of ports) {
    capacityMbps += Number(port.capacity ?? 0);
    for (const key of Object.keys(totals) as AllocationKey[]) {
      totals[key] += Number(port.allocated?.[key] ?? 0);
    }
  }
  const rows = (Object.keys(totals) as AllocationKey[])
    .map((key) => ({ key, label: ALLOCATION_LABELS[key], allocatedMbps: totals[key] }))
    .filter((row) => row.allocatedMbps > 0);
  return {
    capacityMbps,
    allocatedMbps: rows.reduce((sum, row) => sum + row.allocatedMbps, 0),
    rows,
  };
}

// ---------------------------------------------------------------------------
// Maintenance
// ---------------------------------------------------------------------------

export interface MaintenanceEntry {
  key: string;
  date: Date | null;
  dateLabel: string;
  description: string;
  duration: string;
  customer: string | null;
}

/** Customer maintenance from today on, soonest first. */
export function upcomingMaintenance(
  records: MaintenanceItem[],
  tenantScope: TenantScope,
  now: Date
): MaintenanceEntry[] {
  const today = startOfDay(now).getTime();
  return records
    .filter((record) => isInTenantScope(record, tenantScope))
    .map((record, index) => ({ record, index, date: parseRecordDate(record.date) }))
    .filter(({ date }) => date !== null && date.getTime() >= today)
    .sort((a, b) => (a.date as Date).getTime() - (b.date as Date).getTime())
    .map(({ record, index, date }) => ({
      key: `maintenance-${index}-${record.date}`,
      date,
      dateLabel: record.date,
      description: record.description,
      duration: formatDuration(record.duration),
      customer: tenantScope.isViewingAllCustomers ? record.customer?.name ?? null : null,
    }));
}
